import { NgModule, Provider, inject, provideAppInitializer } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { RouterModule, Routes } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";

import "hammerjs";
import { NgbActiveModal, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { ToastrModule } from "ngx-toastr"; // For auth after login toast
import { CoreModule } from "@core/core.module";
import { CoreCommonModule } from "@core/common.module";
import { CoreSidebarModule, CoreThemeCustomizerModule } from "@core/components";

import { coreConfig } from "app/app-config";

import { AppComponent } from "app/app.component";
import { LayoutModule } from "app/layout/layout.module";
import { environment } from "../environments/environment";
import { UsersModule } from "./pages/users/users.module";
import { NgSelectModule } from "@ng-select/ng-select";
import { FormsModule } from "@angular/forms";
import { cdkDragConfigProvider } from "./util/provider/cdk-drag-config.provider";
import { NgxDatatableModule } from "@siemens/ngx-datatable";
import { errorHandlerDistributorProvider } from "./util/error-handler/error-handler-distributor";
import { chunkFailedErrorHandlerProvider } from "./util/error-handler/chunk-failed-error-handler";
import { errorInterceptorProvider } from "./util/interceptor/error.interceptor";
import { SweetAlert2Module } from "@sweetalert2/ngx-sweetalert2";
import { localeProvider } from "./util/provider/locale.provider";
import { AngularIbanModule } from "angular-iban";
import { allIcons, NgxBootstrapIconsModule } from "ngx-bootstrap-icons";
import { ApiConfiguration } from "./api/api-configuration";
import { ServiceWorkerModule } from "@angular/service-worker";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { versionInterceptorProvider } from "./util/interceptor/version.interceptor";
import { abilityProviders } from "./util/service/auth.service";
import { ApiModule } from "./api/api.module";
import { DashboardModule } from "./pages/dashboard/dashboard.module";
import { AuthenticationModule } from "./pages/authentication/authentication.module";
import { OfficesModule } from "./pages/offices/offices.module";
import { MiscellaneousModule } from "./pages/miscellaneous/miscellaneous.module";
import { EmployeesModule } from "./pages/employees/employees.module";
import { HousingModule } from "./pages/housing/housing.module";
import { ClientsModule } from "./pages/clients/clients.module";
import { ReportsModule } from "./pages/reports/reports.module";
import { QuillModule } from "ngx-quill";
import { NgHttpLoaderComponent } from "ng-http-loader";
import { AblePipe } from "@casl/angular";

export function initApiConfiguration(config: ApiConfiguration): Function {
  return () => {
    config.rootUrl = window.location.protocol + "//" + window.location.hostname + ":" + window.location.port;
  };
}

export const INIT_API_CONFIGURATION = provideAppInitializer(() => {
  const initializerFn = initApiConfiguration(inject(ApiConfiguration));
  return initializerFn();
});

const appRoutes: Routes = [
  {
    path: "",
    redirectTo: "/home",
    pathMatch: "full",
  },
  // {
  //   path: "**",
  //   redirectTo: "/error", // Error 404 - Page not found
  // },
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AuthenticationModule,
    DashboardModule,
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: "disabled",
    }),
    TranslateModule.forRoot(),
    // NgBootstrap
    NgbModule,
    ToastrModule.forRoot({
      toastClass: "toast ngx-toastr",
      closeButton: true,
      progressBar: true,
      extendedTimeOut: 5000,
    }),
    // Core modules
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,
    // App modules
    LayoutModule,
    MiscellaneousModule,
    UsersModule,
    ApiModule.forRoot({
      rootUrl: environment.apiUrl,
    }),
    NgSelectModule,
    FormsModule,
    NgxDatatableModule.forRoot({
      messages: {
        emptyMessage: "Keine Einträge gefunden",
        totalMessage: "gesamt",
        selectedMessage: "gewählt",
      },
      headerHeight: 50,
      footerHeight: 50,
    }),
    NgHttpLoaderComponent,
    SweetAlert2Module.forRoot({
      provideSwal: () =>
        import("sweetalert2").then(({ default: swal }) =>
          swal.mixin({
            showCloseButton: true,
          })
        ),
    }),
    AngularIbanModule,
    NgxBootstrapIconsModule.pick(allIcons),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.production,
      registrationStrategy: "registerImmediately",
    }),
    AblePipe,
    OfficesModule,
    EmployeesModule,
    HousingModule,
    ClientsModule,
    ReportsModule,
    QuillModule.forRoot({
      modules: {
        toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }]],
        clipboard: {
          matchers: [
            [
              Node.ELEMENT_NODE,
              (_: any, delta: { ops: any[] }) => {
                const allowedFormats = ["bold", "italic", "underline", "list"];
                delta.ops = delta.ops.map((op) => {
                  if (op.insert && typeof op.insert === "string") {
                    if (op.attributes) {
                      op.attributes = Object.keys(op.attributes)
                        .filter((key) => allowedFormats.includes(key))
                        .reduce((acc, key) => {
                          acc[key] = op.attributes[key];
                          return acc;
                        }, {} as any);
                      if (op.attributes.list) {
                        op.attributes.list = "bullet";
                      }
                    }
                    return op;
                  }
                  return op;
                });
                return delta;
              },
            ],
          ],
        },
      },
      placeholder: "",
    }),
  ],
  providers: [
    INIT_API_CONFIGURATION,
    errorInterceptorProvider,
    versionInterceptorProvider,
    cdkDragConfigProvider,
    errorHandlerDistributorProvider,
    chunkFailedErrorHandlerProvider,
    localeProvider,
    NgbActiveModal,
    provideEnvironmentNgxMask({
      validation: false,
      thousandSeparator: ".",
      decimalMarker: ",",
      outputTransformFn: (value) => (value === "" ? null : value),
    }),
    ...abilityProviders,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
